"use client";
import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { buttonVariants } from "./button";
import { cn } from "@/utils";

type ButtonVariant =
  | "default"
  | "outline"
  | "ghost"
  | "link"
  | "destructive"
  | "secondary"
  | null
  | undefined;

type MonthCell = {
  number: number;
  name: string;
};

const MONTHS: MonthCell[][] = [
  [
    { number: 0, name: "Jan" },
    { number: 1, name: "Feb" },
    { number: 2, name: "Mar" },
    { number: 3, name: "Apr" }
  ],
  [
    { number: 4, name: "May" },
    { number: 5, name: "Jun" },
    { number: 6, name: "Jul" },
    { number: 7, name: "Aug" }
  ],
  [
    { number: 8, name: "Sep" },
    { number: 9, name: "Oct" },
    { number: 10, name: "Nov" },
    { number: 11, name: "Dec" }
  ]
];

type MonthPickerCallbacks = {
  yearLabel?: (year: number) => string;
  monthLabel?: (month: MonthCell) => string;
};

type MonthPickerVariant = {
  calendar?: {
    main?: ButtonVariant;
    selected?: ButtonVariant;
  };
  chevrons?: ButtonVariant;
};

export type MonthPickerProps = {
  selectedDate?: Date;
  onDateSelect?: (date?: Date) => void;
  onYearForward?: () => void;
  onYearBackward?: () => void;
  callbacks?: MonthPickerCallbacks;
  variant?: MonthPickerVariant;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
} & React.HTMLAttributes<HTMLDivElement>;

type MonthCalProps = Pick<
  MonthPickerProps,
  | "selectedDate"
  | "onDateSelect"
  | "onYearForward"
  | "onYearBackward"
  | "callbacks"
  | "variant"
  | "minDate"
  | "maxDate"
  | "disabledDates"
>;

const normalizeMonth = (input?: Date) => {
  if (!input) return undefined;
  const next = new Date(input);
  next.setDate(1);
  next.setHours(0, 0, 0, 0);
  return next;
};

function MonthPicker({
  selectedDate,
  onDateSelect,
  minDate,
  maxDate,
  disabledDates,
  callbacks,
  onYearBackward,
  onYearForward,
  variant,
  className,
  ...props
}: MonthPickerProps) {
  return (
    <div className={cn("min-w-[200px] w-[280px] p-3", className)} {...props}>
      <div className="flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0">
        <div className="space-y-4 w-full">
          <MonthCal
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            callbacks={callbacks}
            onYearBackward={onYearBackward}
            onYearForward={onYearForward}
            variant={variant}
            minDate={minDate}
            maxDate={maxDate}
            disabledDates={disabledDates}
          />
        </div>
      </div>
    </div>
  );
}

function MonthCal({
  selectedDate,
  onDateSelect,
  callbacks,
  variant,
  minDate,
  maxDate,
  disabledDates,
  onYearBackward,
  onYearForward
}: MonthCalProps) {
  const today = React.useMemo(() => {
    const now = new Date();
    now.setDate(1);
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const selectedMonthDate = React.useMemo(() => normalizeMonth(selectedDate), [selectedDate]);
  const min = React.useMemo(() => normalizeMonth(minDate), [minDate]);
  const max = React.useMemo(() => normalizeMonth(maxDate), [maxDate]);

  let effectiveMin = min;
  if (min && max && min > max) {
    effectiveMin = max;
  }

  const minYear = effectiveMin?.getFullYear();
  const minMonth = effectiveMin?.getMonth();
  const maxYear = max?.getFullYear();
  const maxMonth = max?.getMonth();

  const [menuYear, setMenuYear] = React.useState(
    () => selectedMonthDate?.getFullYear() ?? today.getFullYear()
  );

  React.useEffect(() => {
    if (selectedMonthDate) {
      const year = selectedMonthDate.getFullYear();
      if (year !== menuYear) {
        setMenuYear(year);
      }
    }
  }, [selectedMonthDate, menuYear]);

  React.useEffect(() => {
    if (typeof minYear === "number" && menuYear < minYear) {
      setMenuYear(minYear);
      return;
    }
    if (typeof maxYear === "number" && menuYear > maxYear) {
      setMenuYear(maxYear);
    }
  }, [minYear, maxYear, menuYear]);

  const disablePrevYear = typeof minYear === "number" ? menuYear <= minYear : false;
  const disableNextYear = typeof maxYear === "number" ? menuYear >= maxYear : false;

  const yearOptions = React.useMemo(() => {
    const fallbackWindow = 50;
    const start = typeof minYear === "number" ? minYear : menuYear - fallbackWindow;
    const end = typeof maxYear === "number" ? maxYear : menuYear + fallbackWindow;

    const years: number[] = [];
    for (let year = start; year <= end; year++) {
      years.push(year);
    }

    if (!years.includes(menuYear)) {
      years.push(menuYear);
      years.sort((a, b) => a - b);
    }

    return years;
  }, [maxYear, menuYear, minYear]);

  const formatYearLabel = React.useCallback(
    (year: number) => {
      const raw = callbacks?.yearLabel?.(year);
      if (raw === null || raw === undefined) return String(year);
      return typeof raw === "string" ? raw : String(raw);
    },
    [callbacks]
  );

  const handleYearSelect = React.useCallback(
    (nextValue: string) => {
      const nextYear = Number.parseInt(nextValue, 10);
      if (Number.isNaN(nextYear)) return;
      if (typeof minYear === "number" && nextYear < minYear) return;
      if (typeof maxYear === "number" && nextYear > maxYear) return;
      setMenuYear(nextYear);
    },
    [maxYear, minYear]
  );

  const disabledPairs = React.useMemo(() => {
    if (!disabledDates?.length) return [] as { year: number; month: number }[];
    const pairs: { year: number; month: number }[] = [];
    disabledDates.forEach((date) => {
      const normalized = normalizeMonth(date);
      if (normalized) {
        pairs.push({
          year: normalized.getFullYear(),
          month: normalized.getMonth()
        });
      }
    });
    return pairs;
  }, [disabledDates]);

  return (
    <>
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            if (disablePrevYear) return;
            setMenuYear((prev) => prev - 1);
            onYearBackward?.();
          }}
          disabled={disablePrevYear}
          className={cn(
            buttonVariants({ variant: variant?.chevrons ?? "outline" }),
            "inline-flex items-center justify-center h-7 w-7 p-0"
          )}
        >
          <ChevronLeft className="opacity-50 h-4 w-4" />
        </button>

        <Select
          value={String(menuYear)}
          onValueChange={handleYearSelect}
          disabled={disablePrevYear && disableNextYear}
        >
          <SelectTrigger
            size="sm"
            aria-label="Select year"
            className="h-7 w-[100px] justify-center rounded-md border border-gray-200 bg-white px-2 text-sm font-medium text-gray-700 shadow-none focus-visible:ring-0"
          >
            <SelectValue placeholder={formatYearLabel(menuYear)} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {yearOptions.map((year) => (
              <SelectItem
                key={year}
                value={String(year)}
                disabled={
                  (typeof minYear === "number" && year < minYear) ||
                  (typeof maxYear === "number" && year > maxYear)
                }
              >
                {formatYearLabel(year)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          onClick={() => {
            if (disableNextYear) return;
            setMenuYear((prev) => prev + 1);
            onYearForward?.();
          }}
          disabled={disableNextYear}
          className={cn(
            buttonVariants({ variant: variant?.chevrons ?? "outline" }),
            "inline-flex items-center justify-center h-7 w-7 p-0"
          )}
        >
          <ChevronRight className="opacity-50 h-4 w-4" />
        </button>
      </div>

      <table className="w-full border-collapse space-y-1">
        <tbody>
          {MONTHS.map((row, rowIndex) => (
            <tr key={"row-" + rowIndex} className="flex w-full mt-2 gap-2">
              {row.map((m) => {
                const afterMax =
                  maxMonth !== undefined && maxYear !== undefined ?
                    menuYear > maxYear || (menuYear === maxYear && m.number > maxMonth)
                  : false;
                const beforeMin =
                  minMonth !== undefined && minYear !== undefined ?
                    menuYear < minYear || (menuYear === minYear && m.number < minMonth)
                  : false;
                const disabledByList = disabledPairs.some(
                  (d) => d.year === menuYear && d.month === m.number
                );

                const isDisabled = afterMax || beforeMin || disabledByList;
                const isSelected =
                  !isDisabled &&
                  !!selectedMonthDate &&
                  selectedMonthDate.getFullYear() === menuYear &&
                  selectedMonthDate.getMonth() === m.number;

                return (
                  <td key={m.number} className="h-10 w-1/4 text-center text-sm p-0 relative">
                    <button
                      onClick={() => {
                        if (isDisabled) return;
                        if (
                          selectedMonthDate &&
                          selectedMonthDate.getFullYear() === menuYear &&
                          selectedMonthDate.getMonth() === m.number
                        ) {
                          onDateSelect?.(undefined);
                        } else {
                          const next = new Date(menuYear, m.number, 1);
                          next.setHours(0, 0, 0, 0);
                          onDateSelect?.(next);
                        }
                      }}
                      disabled={isDisabled}
                      className={cn(
                        buttonVariants({
                          variant:
                            isSelected ?
                              (variant?.calendar?.selected ?? "default")
                            : (variant?.calendar?.main ?? "ghost")
                        }),
                        "h-9 w-full p-0 text-sm font-normal rounded-md transition-all duration-150",
                        "border border-transparent",

                        isDisabled && "opacity-50 pointer-events-none",

                        isSelected ?
                          "bg-[#379a2a] text-white hover:bg-[#2f8524] active:bg-[#276f1e]"
                        : "bg-white text-black hover:border-[#379a2a] hover:bg-green-50 active:bg-green-100"
                      )}
                    >
                      {callbacks?.monthLabel ? callbacks.monthLabel(m) : m.name}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

MonthPicker.displayName = "MonthPicker";

export { MonthPicker };
